import { Html, OrbitControls } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Vector3 } from 'three';
import TWEEN from 'tween';
import { useShallow } from 'zustand/react/shallow';
import HolesLoader from '../../loaders/HolesLoader.js';
import LoadModel from '../../loaders/LoadModel.js';
import { ModelList } from "../../Utils/ModelsList.js";
import CameraControl from '../../clickTools/CameraControl.js';
import MeasureLines from '../../clickTools/MeasureLines.js';
import { useClickStore, useInforStore } from '../../store/clickStore.js';
import BlockModels from '../../loaders/BlockModels.js';
import AssayLoader from '../../loaders/AssayLoaderTM.js';
import HoleRender from '../../loaders/holeTools/HoleRender.js';
import CachedHoleRender from '../../loaders/holeTools/CachedHoleRender.js';
import GlowingObject from '../../loaders/GlowingObject.js';
import { EffectComposer, Bloom } from '@react-three/postprocessing';
import { useProgress } from '@react-three/drei';
import HighlightedObject from '../../clickTools/HighlightedObject.js';

const SceneComponent = ({ layerVisibility, cameraPosition, lookAt, animateCamera ,currentSlide , onThreeReady, showGrid, fogEnabled }) => {
  const { camera, gl, scene, raycaster } = useThree()
  const controlRef = useRef()
  
  const { progress } = useProgress()

  const constants_ = [259333,8156513,1120]
  const GRID_STEP = 100;
  const GRID_LABEL_DISTANCE = 500;
  const gridOffsetX = (GRID_STEP - (constants_[0] % GRID_STEP)) % GRID_STEP;
  const gridOffsetZ = constants_[1] % GRID_STEP;
  const gridSize = 20000;
  const gridDivisions = gridSize / GRID_STEP;

  // Triple-click detection state
  const clickTimesRef = useRef([])
  const clickPositionsRef = useRef([])
  const TRIPLE_CLICK_TIMEOUT = 500 // milliseconds
  const MAX_CLICK_DISTANCE = 10 // pixels

  const {setInfo} =    useInforStore(useShallow((state)=>({
      setInfo:state.setInfo
  })))

  const {setClickParams, setClickType, clickType, addPinnedInfo} = useClickStore(useShallow((state)=>({        
      setClickParams:state.setClickParams,
      setClickType:state.setClickType,
      clickType:state.clickType,
      addPinnedInfo:state.addPinnedInfo
  })))
  useEffect(()=>{
    onThreeReady({ camera, gl, scene, raycaster , controlRef })
  },[])

  const manage_click=(e)=>{
      console.log(e.point,e.object,e.instanceId)
      
      // Check for triple-click
      const now = Date.now()
      const clickTimes = clickTimesRef.current
      const clickPositions = clickPositionsRef.current
      
      // Add current click time and position
      clickTimes.push(now)
      clickPositions.push({ x: e.point.x, y: e.point.y, z: e.point.z })
      
      // Keep only clicks within the timeout window
      const recentClicks = clickTimes.filter(time => now - time <= TRIPLE_CLICK_TIMEOUT)
      const recentPositions = clickPositions.slice(-recentClicks.length)
      
      // Check if we have 3 clicks and they're close enough spatially
      if (recentClicks.length >= 3) {
        const positions = recentPositions.slice(-3)
        const distances = [
          Math.abs(positions[0].x - positions[1].x) + Math.abs(positions[0].y - positions[1].y) + Math.abs(positions[0].z - positions[1].z),
          Math.abs(positions[1].x - positions[2].x) + Math.abs(positions[1].y - positions[2].y) + Math.abs(positions[1].z - positions[2].z)
        ]
        
        // If clicks are close enough, trigger zoom
        if (distances.every(d => d < 50)) { // 50 units threshold for spatial proximity
          console.log('Triple-click detected! Zooming to point:', e.point)
          // Temporarily set click type to zoom and trigger zoom
          setClickType('zoom')
          setClickParams(e.point, e.object, e.instanceId)
          // Clear the click tracking
          clickTimesRef.current = []
          clickPositionsRef.current = []
          e.stopPropagation()
          return
        }
      }
      
      if (clickType === 'pin') {
        const coords = {
          x: (e.point.x + constants_[0]).toFixed(1),
          y: (e.point.z - constants_[1]).toFixed(1),
          z: (e.point.y + constants_[2]).toFixed(1),
        };
        const instanceData = e.object?.userData?.instancesData?.[e.instanceId];
        const gradeKey = e.object?.userData?.gradeKey;
        const gradeValue = gradeKey && instanceData ? Number(instanceData[gradeKey]) : null;
        const summaryParts = [
          Number.isFinite(gradeValue) ? `Grade:${gradeValue.toFixed(2)}` : null,
          `X:${coords.x} Y:${coords.y} Z:${coords.z}`,
        ].filter(Boolean);
        addPinnedInfo({
          id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
          name: e.object?.name || 'Object',
          summary: summaryParts.join(' | '),
          data: instanceData || e.object?.userData?.displayData || null,
          coords,
        });
        e.stopPropagation();
        return;
      }

      // Normal single click handling
      setClickParams(e.point,e.object,e.instanceId)
      setInfo(e.object?e.object.name+`   coords:X:${(e.point.x+constants_[0]).toFixed(1)} Y:${(e.point.z-constants_[1]).toFixed(1)} Z:${(e.point.y+constants_[2]).toFixed(1)}`:'...')
      e.stopPropagation()        
  }

  
  useEffect(() => {
    if (animateCamera) {
      // If animation is required, tween the camera position and look-at target
      const endPosition = new Vector3(...cameraPosition);
      const endLookAt = new Vector3(...lookAt);

      new TWEEN.Tween(camera.position)
        .to(endPosition, 5000) // Duration of animation in milliseconds
        .easing(TWEEN.Easing.Cubic.Out) // Easing function for smoothness
        .start();

      new TWEEN.Tween(controlRef.current.target)
        .to(endLookAt, 5000) // Duration should match position tween
        .easing(TWEEN.Easing.Cubic.Out)
        .start();

    } else {
      // If no animation, set immediately
      camera.position.set(...cameraPosition);
      controlRef.current.target.set(...lookAt);
    }
  }, [currentSlide]);

  useEffect(() => {
    if (fogEnabled) {
      scene.fog = new THREE.Fog(0xd9d9d9, 2000, 12000);
    } else {
      scene.fog = null;
    }
    return () => {
      scene.fog = null;
    };
  }, [fogEnabled, scene]);

  // Update tweens on each frame
  useFrame(() => {
    TWEEN.update();
    // sconsole.log('camera pos-',camera.position , 'LookAt',controlRef.current.target)
  });



  return (
    <>  
      <directionalLight position={[1,200,-300]} intensity={2.5}/>
      <directionalLight position={[1,200,300]} intensity={2.5}/>
      <ambientLight intensity={1.5}/> 
      <OrbitControls makeDefault ref={controlRef}/>
      {/* <Bloom /> */}
      <MeasureLines />
      {showGrid && (
        <gridHelper
          args={[gridSize, gridDivisions, 0x999999, 0x666666]}
          position={[gridOffsetX, 0, gridOffsetZ]}
        />
      )}
      {showGrid && (
        <GridLineLabels
          origin={constants_}
          gridStep={GRID_STEP}
          gridOffsetX={gridOffsetX}
          gridOffsetZ={gridOffsetZ}
          distance={GRID_LABEL_DISTANCE}
        />
      )}
      {/* Conditionally render objects based on visibleObjects */}
      <group onClick={(e)=>{ manage_click(e)} } >
      {ModelList.map((group) =>
        group.models.map((model) => {
          const modelState = layerVisibility[model.name];
          if (modelState && modelState.visible) {
            return (
              (model.type && model.type=='holes')?
              <HolesLoader key={model.name} name={model.name} url={model.url} dataset={model.dataset} holetype={model.holetype} visible={modelState.visible}/>
              :
              (model.type && model.type=='blockmodel')?
              <BlockModels key={model.name} url={model.url} name={model.name} dx={model.dx} dy={model.dy} dz={model.dz} size={model.size} grade={model.grade} bmvisible={modelState.visible} />
              :
              (model.type && model.type==='assays')?
              <AssayLoader key={model.name} name={model.name} url={model.url} visible={modelState.visible}/>
              :
              (model.type && model.type==='holesassays')?
              <HoleRender key={model.name} name={model.name} hole_url={model.hole_url} assay_url={model.assay_url}  visible={modelState.visible}/>
              :
              (model.type && model.type==='cachedholes')?
              <CachedHoleRender key={model.name} name={model.name} hole_url={model.hole_url} assay_url={model.assay_url} survey_url={model.survey_url} dataset={model.dataset} holetype={model.holetype} visible={modelState.visible} year={model.year} programType={model.programType}/>
              :
              <LoadModel key={model.name} name={model.name} url={model.url} visible={modelState.visible} shouldBloom={model.shouldBloom?model.shouldBloom:false}/>
            );
          }
          return null;
        })
      )}
      </group>
      {/* <GlowingObject /> */}
      <HighlightedObject />
      <CameraControl controls={controlRef}/>
      <ambientLight intensity={0.5} />
      <directionalLight position={[5, 5, 5]} intensity={0.5} />  
      <Levels/>  
      {/* <EffectComposer>
      <Bloom  mipmapBlur={0}
              luminanceThreshold={0.9}
              luminanceSmoothing={0.025}
              intensity={2}
              radius={0.5}
      />
    </EffectComposer> */}
    </>  
  );
};

function Levels() {
  // const { camera } = useThree()
  // const [isVisible, setIsVisible] = useState(true);

  
  const {showLabels} = useClickStore()

  var levels = [
      ["2L",1120],
      ["3L",1086],
      ["4L",1055],
      ["5L",1013],
      ["6L",934],
      ["7L",888],
      ["8L",847],
      ["9L",801],
      ["10L",754],
      ["11L",667],
      ["12L",595],
      ["13L",522],
      ["14L",445],
      ["15L",375],
      ["16L",305],
      ["17L",235],
      ["18L",165],
      ["19L",95],
      ["20L",25],
      ["SEA LEVEL",0],
      ["21L",-45],
      ["22L",-115],
      ["23L",-185],
      ["24L",-255]
  ]

  // useFrame(({ camera }) => {
  //     const distance = camera.position.distanceTo(new Vector3(205650-259333, 800-1120, 8156513-7975010));
  //     setIsVisible(distance <= 5000);
  //     //console.log('distance checked',distance,distance <= 2000)
  //   });



  return (
      <>
         {showLabels && levels.map((a, i) => {
              return (
                  <Html 
                      key={i} 
                      position={[205650-259333, a[1]-1120, 8156513-7975010]}
                      occlude="blending"
                      center
                      transform
                      sprite
                      distanceFactor={500}
                      >
                      <div className="levels" onClick={() => console.log('.')}>
                          {a[0] + '-' + (1120-a[1])+'m (' + a[1]+'RL)'} 
                      </div>
                      
                  </Html>
              )
          })
          }
      </>
  )
}

function GridLineLabels({ origin, gridStep, gridOffsetX, gridOffsetZ, distance }) {
  const posX =
    gridOffsetX + Math.ceil((distance - gridOffsetX) / gridStep) * gridStep;
  const negX =
    gridOffsetX - Math.ceil((distance + gridOffsetX) / gridStep) * gridStep;
  const posZ =
    gridOffsetZ + Math.ceil((distance - gridOffsetZ) / gridStep) * gridStep;
  const negZ =
    gridOffsetZ - Math.ceil((distance + gridOffsetZ) / gridStep) * gridStep;

  const labelPositions = [
    {
      pos: [posX, 0, 0],
      text: `X ${Math.round((origin[0] + posX) / gridStep) * gridStep}`,
    },
    {
      pos: [negX, 0, 0],
      text: `X ${Math.round((origin[0] + negX) / gridStep) * gridStep}`,
    },
    {
      pos: [0, 0, posZ],
      text: `Y ${Math.round((origin[1] - posZ) / gridStep) * gridStep}`,
    },
    {
      pos: [0, 0, negZ],
      text: `Y ${Math.round((origin[1] - negZ) / gridStep) * gridStep}`,
    },
  ];

  return (
    <>
      {labelPositions.map((label) => (
        <Html
          key={label.text}
          position={[label.pos[0], label.pos[1] + 1, label.pos[2]]}
          center
          transform
          sprite
          distanceFactor={400}
          style={{ pointerEvents: 'none' }}
        >
          <div className="text-[10px] text-white bg-black/40 px-1 rounded">
            {label.text}
          </div>
        </Html>
      ))}
    </>
  );
}

export default SceneComponent;
